// Theme Context for dark/light/system theme support with Ant Design integration
// Syncs theme preference to Firestore for cross-context sync (extension ↔ web dashboard)
import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ConfigProvider } from 'antd';
import { darkTheme, lightTheme } from '../theme/antd-theme.js';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../services/firebase-config.js';
import { onAuthStateChanged } from 'firebase/auth';

const ThemeContext = createContext(null);

export const THEMES = {
    DARK: 'dark',
    LIGHT: 'light',
    SYSTEM: 'system'
};

// Map extension theme names to web dashboard theme names
// Extension uses: 'light', 'dark', 'dracula'
// Web uses: 'light', 'dark', 'system'
// 'dracula' from extension maps to 'dark' on web (closest match)
function normalizeThemeFromFirestore(firestoreTheme) {
    if (firestoreTheme === 'dracula') return THEMES.DARK;
    if (firestoreTheme === 'dark') return THEMES.DARK;
    if (firestoreTheme === 'light') return THEMES.LIGHT;
    return firestoreTheme || THEMES.DARK;
}

// Map web dashboard theme to Firestore value
function themeToFirestore(webTheme) {
    // 'system' is web-only, resolve it before storing
    if (webTheme === THEMES.SYSTEM) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return webTheme;
}

export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(() => {
        const saved = localStorage.getItem('page-notes-theme');
        return saved || THEMES.DARK;
    });

    const [resolvedTheme, setResolvedTheme] = useState(THEMES.DARK);

    // Track whether the theme change came from Firestore (to avoid write-back loops)
    const isRemoteUpdate = useRef(false);

    // Set theme with Firestore sync
    const setTheme = useCallback((newTheme) => {
        isRemoteUpdate.current = false;
        setThemeState(newTheme);
    }, []);

    useEffect(() => {
        // Resolve system theme
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        const resolveTheme = () => {
            if (theme === THEMES.SYSTEM) {
                return mediaQuery.matches ? THEMES.DARK : THEMES.LIGHT;
            }
            return theme;
        };

        const updateResolvedTheme = () => {
            const resolved = resolveTheme();
            setResolvedTheme(resolved);
            document.documentElement.setAttribute('data-theme', resolved);
        };

        updateResolvedTheme();
        mediaQuery.addEventListener('change', updateResolvedTheme);

        return () => mediaQuery.removeEventListener('change', updateResolvedTheme);
    }, [theme]);

    // Persist to localStorage and sync to Firestore
    useEffect(() => {
        localStorage.setItem('page-notes-theme', theme);

        // Don't write back to Firestore if this was a remote update (prevents loops)
        if (isRemoteUpdate.current) {
            isRemoteUpdate.current = false;
            return;
        }

        // Sync to Firestore for cross-context sync
        const user = auth.currentUser;
        if (user) {
            const prefsRef = doc(db, 'users', user.uid, 'settings', 'preferences');
            setDoc(prefsRef, { theme: themeToFirestore(theme), updatedAt: new Date() }, { merge: true })
                .catch((err) => console.warn('[ThemeContext] Failed to sync theme to Firestore:', err));
        }
    }, [theme]);

    // Subscribe to real-time theme changes from Firestore
    useEffect(() => {
        let unsubscribeSnapshot = null;

        const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
            // Clean up previous snapshot listener
            if (unsubscribeSnapshot) {
                unsubscribeSnapshot();
                unsubscribeSnapshot = null;
            }

            if (user) {
                const prefsRef = doc(db, 'users', user.uid, 'settings', 'preferences');
                unsubscribeSnapshot = onSnapshot(prefsRef, (snapshot) => {
                    if (!snapshot.exists()) return;

                    const data = snapshot.data();
                    const remoteTheme = data.theme;
                    if (!remoteTheme) return;

                    const normalizedTheme = normalizeThemeFromFirestore(remoteTheme);

                    // Only update if different from current theme
                    setThemeState((currentTheme) => {
                        if (normalizedTheme !== currentTheme) {
                            console.log('[ThemeContext] Remote theme change:', remoteTheme, '→', normalizedTheme);
                            isRemoteUpdate.current = true;
                            return normalizedTheme;
                        }
                        return currentTheme;
                    });
                }, (err) => {
                    console.warn('[ThemeContext] Theme sync listener error:', err);
                });
            }
        });

        return () => {
            unsubscribeAuth();
            if (unsubscribeSnapshot) {
                unsubscribeSnapshot();
            }
        };
    }, []);

    // Get the appropriate Ant Design theme based on resolved theme
    const antdTheme = useMemo(() => {
        return resolvedTheme === THEMES.DARK ? darkTheme : lightTheme;
    }, [resolvedTheme]);

    const isDark = resolvedTheme === THEMES.DARK;

    return (
        <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme, antdTheme, isDark }}>
            <ConfigProvider theme={antdTheme}>
                {children}
            </ConfigProvider>
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}
